import { apiService } from 'core/services/apiService';
import { paginationService } from 'core/services/paginationService';
import type { JobCode } from 'core/API/scheduler';
import { parseUserIdFromRef } from 'core/utils/checklistEventAssignment';
import { parseJobCodeId } from 'module/schedule/utils/jobCodeEligibility';

export const USER_JOB_CODES_COLLECTION = 'user_job_codes';

export interface UserJobCodeUserRef {
	'@context'?: string;
	'@id'?: string;
	'@type'?: string;
	id?: number;
	firstName?: string;
	middleName?: string;
	lastName?: string;
	email?: string;
}

export interface UserJobCode {
	'@context'?: string;
	'@id'?: string;
	'@type'?: string;
	id: number;
	user: UserJobCodeUserRef | string;
	jobCode: JobCode | string;
}

export interface CreateUserJobCodePayload {
	user: string;
	jobCode: string;
}

export interface BatchCreateUserJobCodesPayload {
	user: string;
	jobCodes: string[];
}

export interface UpdateUserJobCodePayload {
	user: string;
	jobCode: string;
}

export const buildUserIri = (userId: number | string): string => `/api/users/${userId}`;

export const buildJobCodeApiIri = (jobCodeId: number | string): string =>
	`/api/job_codes/${jobCodeId}`;

const extractMember = (data: any): UserJobCode[] =>
	data?.member || data?.['hydra:member'] || (Array.isArray(data) ? data : []);

export const getUserJobCodeItemEndpoint = (id: number | string): string =>
	`${USER_JOB_CODES_COLLECTION}/${id}`;

/** List user↔job-code associations. Optionally filter by user id (`?user=`). */
export const getUserJobCodes = async (options?: {
	userId?: number | string;
}): Promise<UserJobCode[]> => {
	let endpoint: string = USER_JOB_CODES_COLLECTION;
	if (options?.userId != null && options.userId !== '') {
		endpoint = `${USER_JOB_CODES_COLLECTION}?user=${options.userId}`;
	}
	const data = await paginationService.getWithHandler(endpoint);
	return extractMember(data);
};

export const getUserJobCode = async (id: number | string): Promise<UserJobCode> => {
	const response = await apiService.get(getUserJobCodeItemEndpoint(id));
	return apiService.responseHandler(response);
};

export const createUserJobCode = async (
	payload: CreateUserJobCodePayload,
): Promise<UserJobCode> => {
	const response = await apiService.post(USER_JOB_CODES_COLLECTION, payload);
	return apiService.responseHandler(response);
};

/** Replaces the user's job codes with `payload.jobCodes` (backend replace semantics). */
export const batchCreateUserJobCodes = async (
	payload: BatchCreateUserJobCodesPayload,
): Promise<UserJobCode[] | UserJobCode> => {
	const response = await apiService.post(`${USER_JOB_CODES_COLLECTION}/batch`, payload);
	const data = apiService.responseHandler(response);
	return data;
};

export const updateUserJobCode = async (
	id: number | string,
	payload: UpdateUserJobCodePayload,
): Promise<UserJobCode> => {
	const response = await apiService.put(getUserJobCodeItemEndpoint(id), payload);
	return apiService.responseHandler(response);
};

export const deleteUserJobCode = async (id: number | string): Promise<void> => {
	await apiService.delete(getUserJobCodeItemEndpoint(id));
};

export const getJobCodeIdFromUserJobCode = (row: UserJobCode): number | null =>
	parseJobCodeId(row.jobCode);

export const getUserIdFromUserJobCode = (row: UserJobCode): number | null =>
	parseUserIdFromRef(row.user);

/**
 * Replace a user's job-code eligibility with `desiredJobCodeIds`.
 * The batch endpoint replaces the user's job codes, so always send the full desired list.
 * Clearing all codes deletes the existing associations individually.
 */
export const syncUserJobCodes = async (
	userId: number,
	desiredJobCodeIds: number[],
): Promise<void> => {
	const desired = Array.from(new Set(desiredJobCodeIds.filter((id) => Number.isFinite(id))));
	const existing = await getUserJobCodes({ userId });

	const existingJobCodeIds = new Set<number>();
	for (const row of existing) {
		const jobCodeId = getJobCodeIdFromUserJobCode(row);
		if (jobCodeId != null && row.id != null) {
			existingJobCodeIds.add(jobCodeId);
		}
	}

	const hasChanges =
		desired.length !== existingJobCodeIds.size ||
		desired.some((id) => !existingJobCodeIds.has(id));
	if (!hasChanges) return;

	if (desired.length > 0) {
		await batchCreateUserJobCodes({
			user: buildUserIri(userId),
			jobCodes: desired.map((id) => buildJobCodeApiIri(id)),
		});
		return;
	}

	await Promise.all(
		existing.filter((row) => row.id != null).map((row) => deleteUserJobCode(row.id)),
	);
};

/** Map user id → job code ids from a list of associations. */
export const groupJobCodeIdsByUserId = (
	rows: UserJobCode[],
): Map<number, number[]> => {
	const map = new Map<number, number[]>();
	for (const row of rows) {
		const userId = getUserIdFromUserJobCode(row);
		const jobCodeId = getJobCodeIdFromUserJobCode(row);
		if (userId == null || jobCodeId == null) continue;
		const list = map.get(userId) || [];
		if (!list.includes(jobCodeId)) list.push(jobCodeId);
		map.set(userId, list);
	}
	return map;
};
